import React, { useEffect, useRef, useState } from 'react';
import { chakra, Image, useMediaQuery } from '@chakra-ui/react';

type ImageSrc = {
  src: string;
  size?: string;
  default?: boolean
}



export type SmartImageProps = {
  /**
   * A set of image src. The first item is always the default
   */
  src: ImageSrc[] | string;
  /**
   * Only load the image when the element is in view. If not set, the image will be loaded as soon as the page is loaded
   */
  defer?: boolean;
  // Provided by Chakra
  className?: string;

  // Placeholder dimensions for when image is not loaded
  placeholderWidth?: string;
  placeholderHeight?: string;

  style?: Record<any, any>;
  alt?: string;
}

const trimComma = (s: string) => s.endsWith(',') ? s.slice(0, -1) : s

export default chakra(function SmartImage({
  defer = false,
  className,
  src,
  placeholderWidth,
  placeholderHeight,
  style,
  alt
}: SmartImageProps) {

  if (!src) return null;

  const [canLoad, setCanLoad] = useState(!defer);
  const [loaded, setLoaded] = useState(false);
  const ele = useRef<HTMLImageElement>();

  const [isMobile] = useMediaQuery("(max-width: 900px)")

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.intersectionRatio > 0.0) {
            setCanLoad(true)
            observer.disconnect();
          }
        });
      },
      {
        rootMargin: '400px 0px'
      }
    )

    observer.observe(ele.current);
  }, [])

  const baseSrcSet = typeof src === 'string' ? src : src.reduce((acc, { src, size = '' }, index) => {
    if (src.endsWith('webp')) return acc;
    const isLast = index === src.length - 1;
    if (isMobile && size) return acc;
    return acc + `${src}${size ? ' ' : ''}${size}${isLast ? '' : ',\n'}`
  }, '').trim()

  const webPSourceSet = typeof src === 'string' ? '' : src.reduce((acc, { src, size = '' }, index) => {
    if (!src.endsWith('webp')) return acc;
    const isLast = index === src.length - 1;
    if (isMobile && size) return acc;
    return acc + `${src}${size ? ' ' : ''}${size}${isLast ? '' : ',\n'}`
  }, '').trim()

  const defaultSource = typeof src === 'string' ? src : src.find(s => s.default === true)?.src || src[0].src;

  return (
    <picture ref={ele}>
      {
        webPSourceSet &&
        <source  className={className} type='image/webp' srcSet={trimComma(webPSourceSet)} />
      }
      
      <Image
        className={className}
        src={canLoad ? defaultSource : undefined}
        srcSet={canLoad && !isMobile ? trimComma(baseSrcSet) : undefined}
        opacity={!canLoad ? 0 : undefined}
        onLoad={() => setLoaded(true)}
        width={!loaded && placeholderWidth ? placeholderWidth : undefined}
        height={!loaded && placeholderHeight ? placeholderHeight : undefined}
        style={style}
        alt={alt}
        fallbackSrc={defaultSource}
      />
    </picture>

  )
})